import { useCallback, useEffect, useMemo, useState } from "react";
import {
	useAlertContext,
	useClinicContext,
	useUserContext
} from "../../contexts";
import { AlertType, BannedPatient, Clinic } from "../../models";
import { SelectControl } from "../Common";
import { BuildUrl, formatCentreName } from "../../helpers";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest, ROUTES } from "../../constants";
import CircularProgress from "@mui/material/CircularProgress";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { BannedPatientsTable } from "./BannedPatientsTable";
import { useNavigate } from "react-router-dom";
import { FromPage } from "../../pages/PatientSearch";
import { parse } from "date-fns/parse";
import { format } from "date-fns/format";
import { SearchPatientInput } from "../PatientSearch";

interface props {
	clinic: Clinic | undefined;
	setClinic: React.Dispatch<React.SetStateAction<Clinic | undefined>>;
}
export const BannedPatients = ({ clinic, setClinic }: props) => {
	const { userClinics } = useClinicContext();
	const { account } = useUserContext();
	const { showAlert } = useAlertContext();
	const { instance } = useMsal();
	const navigate = useNavigate();
	const [bannedPatients, setBannedPatients] = useState<BannedPatient[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchValue, setSearchValue] = useState<string>("");

	const getBannedPatients = useCallback(
		async (clinicId: number) => {
			try {
				setIsLoading(true);
				const requestUrl = BuildUrl(`Patient/BannedPatient/clinic/${clinicId}`);
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const headers = {
					...(tokenResponse && {
						Authorization: `Bearer ${tokenResponse.accessToken}`
					}),
					...{ "Content-Type": "application/json" }
				};
				const response = await fetch(requestUrl, {
					method: "GET",
					headers
				});

				if (!response.ok) {
					throw new Error("Network response was not ok");
				}

				const contentLength = response.headers.get("Content-Length");
				if (response.status !== 204 && contentLength !== "0") {
					const result = await response.json();
					console.info("getBannedPatients", result);
					setBannedPatients(
						result
							.map((bannedPatient: BannedPatient) => ({
								...bannedPatient,
								dateOfBirth: bannedPatient.dateOfBirth
									? format(
											parse(
												bannedPatient.dateOfBirth,
												"yyyy-MM-dd",
												new Date()
											),
											"dd-MM-yyyy"
										)
									: undefined,
								clinicName: formatCentreName(bannedPatient.clinicName)
							}))
							.sort((a: BannedPatient, b: BannedPatient) =>
								b.patientName > a.patientName ? -1 : 0
							)
					);
				}
			} catch (error) {
				console.info(error);
				showAlert({
					message: "Banned patients could not be retrieved",
					alertType: AlertType.error
				});
				return;
			} finally {
				setIsLoading(false);
			}
		},
		[account, instance, showAlert]
	);

	useEffect(() => {
		if (!clinic && userClinics && userClinics.length > 0) {
			setClinic(userClinics[0]);
		}
		if (clinic) {
			getBannedPatients(clinic.id);
		}
	}, [clinic, getBannedPatients, setClinic, userClinics]);

	const clinicOptions = useMemo(
		() =>
			userClinics?.map((clinic) => ({
				value: clinic.id.toString(),
				label: clinic.name
			})),
		[userClinics]
	);

	const handleClinicChange = (value: string) => {
		const clinic = userClinics?.find((clinic) => clinic.id === Number(value));
		setClinic(clinic);
	};

	const handleAddClick = useCallback(() => {
		navigate(ROUTES.patientSearch, {
			state: {
				clinic: clinic,
				fromPage: FromPage.BannedPatient,
				bannedPatients: bannedPatients
			}
		});
	}, [bannedPatients, clinic, navigate]);

	const handleAddMessageClick = useCallback(() => {
		navigate(ROUTES.bannedPatientMessage, {
			state: {
				clinic: clinic
			}
		});
	}, [clinic, navigate]);

	const refreshPatients = useCallback(() => {
		if (clinic) {
			getBannedPatients(clinic.id);
		}
	}, [clinic, getBannedPatients]);

	const handleSearchInput = useCallback((value: string) => {
		setSearchValue(value.trim());
	}, []);

	return (
		<>
			<div className="flex justify-start gap-8">
				<div style={{ width: 250 }}>
					<SelectControl
						id="select-clinic"
						label="Centre"
						value={clinic?.id?.toString() ?? ""}
						options={clinicOptions}
						handleChange={handleClinicChange}
					/>
				</div>
				<div className="ch-search-patient-input">
					<SearchPatientInput
						setSearchValue={handleSearchInput}
						label={"Search by Name"}
					/>
				</div>
				<div className="flex items-center">
					<AddCircleOutlineOutlinedIcon
						className="icon"
						style={{
							fontSize: 48
						}}
						onClick={() => {
							handleAddClick();
						}}
					/>
					<div
						className="ml-2"
						style={{ cursor: "pointer" }}
						onClick={() => {
							handleAddClick();
						}}
					>
						Add Banned Patient
					</div>
				</div>
				<button
					className="ch-button ch-button-primary ch-button-center-text"
					onClick={() => handleAddMessageClick()}
					dangerouslySetInnerHTML={{
						__html: "App Check-In<br/>Message Template"
					}}
				></button>
			</div>

			<div className="flex flex-col py-4 gap-4">
				{isLoading && (
					<div className="flex justify-center">
						<CircularProgress size="80px" />
					</div>
				)}
				{!isLoading && clinic && (
					<>
						<BannedPatientsTable
							bannedPatients={bannedPatients}
							searchText={searchValue}
							clinic={clinic}
							refreshPatients={refreshPatients}
						/>
					</>
				)}
			</div>
		</>
	);
};
